import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import Dumbbell from 'highcharts/modules/dumbbell';
import Lollipop from 'highcharts/modules/lollipop';
import Funnel from 'highcharts/modules/funnel';
import WordCloud from 'highcharts/modules/wordcloud';
import Heatmap from 'highcharts/modules/heatmap';
import Sankey from 'highcharts/modules/sankey';
import Treemap from 'highcharts/modules/treemap';
import DependencyWheel from 'highcharts/modules/dependency-wheel';
import SolidGauge from 'highcharts/modules/solid-gauge';
import Accessibility from 'highcharts/modules/accessibility';
import { ZiChartOptions } from '../types/charts/chart-options';
import { useLocation } from 'react-router-dom';

HighchartsMore(Highcharts);
WordCloud(Highcharts);
Funnel(Highcharts);
Dumbbell(Highcharts);
Lollipop(Highcharts);
Heatmap(Highcharts);
Sankey(Highcharts);
Treemap(Highcharts);
DependencyWheel(Highcharts);
SolidGauge(Highcharts);
Accessibility(Highcharts);

type RefObjectForHighchartsReact = {
	/**
	 * Chart reference
	 */
	chart: Highcharts.Chart;
	/**
	 * React reference
	 */
	container: React.RefObject<HTMLDivElement>;
};

export const useHighChart = (
	chartRenderCallback: () => void
): {
	options: ZiChartOptions;
	setOptions: (options: ZiChartOptions) => void;
	chartRender: JSX.Element;
	getChart: () => Highcharts.Chart | undefined;
} => {
	const [chartOptions, setChartOptions] = useState<ZiChartOptions>({});
	const pathname = useLocation().pathname;
	const isDashboardView = pathname.includes('analyze/dashboards/view/');

	const chartRef = useRef<RefObjectForHighchartsReact>(null);

	useEffect(() => {
		if (
			chartRef.current?.chart.chartHeight != chartOptions.height ||
			chartRef.current?.chart.chartWidth != chartOptions.width
		) {
			chartRef.current?.chart.setSize(
				(chartOptions.width as number) ?? 0,
				(chartOptions.height as number) ?? 0
			);
		}
	}, [chartOptions.height, chartOptions.width]);

	useEffect(() => {
		if (
			chartRef.current &&
			chartRef.current?.chart &&
			['wordcloud', 'heatmap', 'treemap', 'sankey'].includes(
				chartOptions.chart?.type as string
			)
		) {
			chartRef.current.chart.reflow();
		}
	}, [chartOptions.series, chartRef.current]);

	void document.fonts.ready.then(() => {
		chartRef.current?.chart.reflow();
	});

	const getChart = useCallback(() => {
		return chartRef.current?.chart;
	}, []);

	const updateOptions = useCallback((options: ZiChartOptions) => {
		setChartOptions(options);
	}, []);

	const chartKey = useMemo(() => {
		const type = chartOptions.chart?.type;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		const seriesHash = JSON.stringify(chartOptions.series?.[0]?.data?.map((d: any) => d));

		// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
		return `${type}-${seriesHash}`;
	}, [chartOptions]);

	const forceRemountOnTypeChange = useRef<string | undefined>();

	const immutable = useMemo(() => {
		// This is to force chart reload on chart type change
		// Seems to be a bug in some cases when updating chart type from bar to column where it does not update correctly
		const type = chartOptions.chart?.type;
		const changed = forceRemountOnTypeChange.current !== type;
		forceRemountOnTypeChange.current = type;

		if (type === 'wordcloud') {
			return true;
		}
		if (isDashboardView) {
			return false;
		}

		return changed;
	}, [chartOptions.chart?.type, isDashboardView]);

	const optionsWithSeriesType = useMemo<ZiChartOptions>(() => {
		if (!chartOptions.series) return chartOptions;

		return {
			...chartOptions,
			chart: {
				...chartOptions.chart,
				style: {
					...chartOptions.chart?.style,
					fontFamily: "'Inter', 'RedHatText', 'Overpass', overpass, helvetica",
				},
			},
			series: chartOptions.series.map((s) => ({
				...s,
				type: chartOptions.chart?.type ?? s.type,
			})),
		} as ZiChartOptions;
	}, [chartOptions]);

	return {
		options: chartOptions,
		setOptions: updateOptions,
		chartRender: (
			<div
				style={{
					height: '100%',
					width: '100%',
				}}
			>
				{optionsWithSeriesType.title &&
					optionsWithSeriesType.series &&
					optionsWithSeriesType.series.length > 0 &&
					optionsWithSeriesType.series[0].type && (
						<HighchartsReact
							ref={chartRef}
							highcharts={Highcharts}
							options={optionsWithSeriesType}
							containerProps={{ style: { height: '100%' } }}
							immutable={immutable}
							callback={chartRenderCallback}
						/>
					)}
			</div>
		),
		getChart,
	};
};
